import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import { ScanLine } from 'lucide-react';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { useToast } from '@/hooks/use-toast';
import { getFieldsForTipo, applyDerived } from '@/lib/inventario/fieldSchemas';
import { DatosTecnicosFields } from '@/components/inventario/DatosTecnicosFields';
import { QRScannerDialog } from '@/components/inventario/QRScannerDialog';
import { parseExtintorQR } from '@/lib/qrParserUtils';

interface ElementoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ID del sistema al que pertenece el elemento. */
  sistemaId: string;
  /** Tipo del sistema (sistemas.tipo, sin normalizar) — para filtrar catálogos. */
  sistemaTipo: string;
  /** Si se pasa, edita; si es null/undefined, crea uno nuevo. */
  elemento?: any | null;
  /** Se invoca tras crear o actualizar. Recibe el ID del elemento. */
  onSaved?: (elementoId: string) => void;
}

const normalizar = (txt: string | null | undefined) =>
  (txt ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const emptyForm = () => ({
  tipo_equipo: '',
  ubicacion: '',
  fecha_instalacion: '',
  marca: '',
  modelo: '',
  mac_serie: '',
  anio_fabricacion: '',
  observaciones: '',
  estado: 'activo',
  datos_tecnicos: {} as Record<string, any>,
});

/**
 * Diálogo reutilizable para crear / editar un elemento de un sistema.
 * Usa MaintenanceContext para los catálogos (tipos / marcas / modelos)
 * y para persistir el elemento. Sustituye el formulario inline anterior
 * tanto en la pantalla "Listado de elementos" como en la revisión.
 */
export const ElementoEditDialog: React.FC<ElementoEditDialogProps> = ({
  open,
  onOpenChange,
  sistemaId,
  sistemaTipo,
  elemento,
  onSaved,
}) => {
  const { toast } = useToast();
  const {
    tiposEquipo,
    marcasEquipo,
    modelosEquipo,
    recursosOcultos,
    crearElemento,
    editarElemento,
    crearTipoEquipo,
    crearMarcaEquipo,
    crearModeloEquipo,
    isLoading,
  } = useSecurityContext();

  const [formData, setFormData] = useState(emptyForm);
  const [nuevoTipoEquipo, setNuevoTipoEquipo] = useState('');
  const [qrDialogAbierto, setQrDialogAbierto] = useState(false);
  const qrJustClosedRef = useRef(false);

  const editandoId: string | null = elemento?.id ?? null;
  const sistemaTipoNormalizado = useMemo(() => normalizar(sistemaTipo), [sistemaTipo]);

  // Reset / carga al abrir
  useEffect(() => {
    if (!open) return;
    if (elemento) {
      setFormData({
        tipo_equipo: elemento.tipo || '',
        ubicacion: elemento.zona || '',
        fecha_instalacion: elemento.fecha_instalacion || '',
        marca: elemento.marca || '',
        modelo: elemento.modelo || '',
        mac_serie: elemento.mac_serie || '',
        anio_fabricacion: elemento.anio_fabricacion || '',
        observaciones: elemento.observaciones || '',
        estado: elemento.estado || 'activo',
        datos_tecnicos: (elemento.datos_tecnicos as Record<string, any>) || {},
      });
    } else {
      setFormData(emptyForm());
    }
    setNuevoTipoEquipo('');
  }, [open, elemento]);

  // Catálogos filtrados por sistema
  const marcasDelSistema = useMemo(
    () =>
      marcasEquipo.filter(
        (m) =>
          normalizar(m.sistema_tipo) === sistemaTipoNormalizado &&
          !recursosOcultos.marcas.has(m.id),
      ),
    [marcasEquipo, recursosOcultos.marcas, sistemaTipoNormalizado],
  );
  const selectedMarca = marcasDelSistema.find((m) => m.nombre === formData.marca);
  const tipoEquipoActual =
    formData.tipo_equipo && formData.tipo_equipo !== 'nuevo' ? formData.tipo_equipo : null;
  const modelosBase = useMemo(
    () =>
      modelosEquipo.filter(
        (md) =>
          normalizar(md.sistema_tipo) === sistemaTipoNormalizado &&
          !recursosOcultos.modelos.has(md.id) &&
          (!selectedMarca || md.marca_id === selectedMarca.id),
      ),
    [modelosEquipo, recursosOcultos.modelos, sistemaTipoNormalizado, selectedMarca],
  );
  const modelosPorTipo = tipoEquipoActual
    ? modelosBase.filter(
        (m) => m.tipo_equipo && normalizar(m.tipo_equipo) === normalizar(tipoEquipoActual),
      )
    : modelosBase;
  const modelosDelSistema = modelosPorTipo.length > 0 ? modelosPorTipo : modelosBase;

  const esMismoSistema = (tipoSistema: string) =>
    normalizar(tipoSistema) === sistemaTipoNormalizado;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sistemaId || !formData.tipo_equipo || !formData.ubicacion) {
      toast({
        title: 'Error',
        description: 'Por favor completa el tipo de equipo y la ubicación.',
        variant: 'destructive',
      });
      return;
    }
    if (
      formData.anio_fabricacion &&
      !/^\d{4}$|^\d{2}\/\d{4}$/.test(formData.anio_fabricacion)
    ) {
      toast({
        title: 'Formato incorrecto',
        description: 'El año de fabricación debe ser AAAA o MM/AAAA (ej: 2024 o 03/2024).',
        variant: 'destructive',
      });
      return;
    }

    const nombreFinal = `${formData.tipo_equipo} - ${formData.ubicacion}`;
    const fields = getFieldsForTipo(formData.tipo_equipo);
    const datosTecnicos = applyDerived(fields, formData.datos_tecnicos || {});

    const elementoData: any = {
      nombre: nombreFinal,
      tipo: formData.tipo_equipo,
      zona: formData.ubicacion || null,
      fecha_instalacion: formData.fecha_instalacion || null,
      marca: formData.marca || null,
      modelo: formData.modelo || null,
      mac_serie: formData.mac_serie || null,
      anio_fabricacion: formData.anio_fabricacion || null,
      observaciones: formData.observaciones || '',
      estado: editandoId ? formData.estado : 'activo',
      sistema_id: sistemaId,
      revisado: false,
      datos_tecnicos: datosTecnicos,
    };

    if (editandoId) {
      await editarElemento(editandoId, elementoData);
      onSaved?.(editandoId);
    } else {
      const creado: any = await crearElemento(elementoData);
      if (creado?.id) onSaved?.(creado.id);
    }

    onOpenChange(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o && (qrDialogAbierto || qrJustClosedRef.current)) return;
          onOpenChange(o);
        }}
      >
        <DialogContent className="sm:max-w-[720px] mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editandoId ? 'Editar elemento' : 'Crear nuevo elemento'}</DialogTitle>
            <DialogDescription>
              {editandoId
                ? 'Modifica los datos del elemento seleccionado.'
                : 'Completa los datos para crear un nuevo elemento del sistema.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tipo de equipo */}
            <div className="space-y-2">
              <Label htmlFor="tipo_equipo">Tipo de equipo *</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.tipo_equipo}
                  onValueChange={(value) => {
                    if (value === 'nuevo') {
                      setNuevoTipoEquipo('');
                      setFormData({ ...formData, tipo_equipo: 'nuevo', datos_tecnicos: {} });
                    } else {
                      setFormData({ ...formData, tipo_equipo: value, datos_tecnicos: {} });
                    }
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Seleccionar tipo..." />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border border-border z-50">
                    {tiposEquipo
                      .filter((tipo) => esMismoSistema(tipo.sistema_tipo || ''))
                      .filter((tipo) => !recursosOcultos.tipos.has(tipo.id))
                      .map((tipo) => (
                        <SelectItem key={tipo.id} value={tipo.nombre}>
                          {tipo.nombre}
                        </SelectItem>
                      ))}
                    <SelectItem value="nuevo">+ Añadir nuevo tipo</SelectItem>
                  </SelectContent>
                </Select>
                {formData.tipo_equipo === 'nuevo' && (
                  <div className="flex gap-2 flex-1">
                    <Input
                      placeholder="Nuevo tipo..."
                      value={nuevoTipoEquipo}
                      onChange={(e) => setNuevoTipoEquipo(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (nuevoTipoEquipo.trim()) {
                          await crearTipoEquipo(nuevoTipoEquipo.trim(), sistemaTipo);
                          setFormData({ ...formData, tipo_equipo: nuevoTipoEquipo.trim() });
                          setNuevoTipoEquipo('');
                        }
                      }}
                    >
                      +
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Ubicación */}
            <div className="space-y-2">
              <Label htmlFor="ubicacion">Ubicación *</Label>
              <Input
                id="ubicacion"
                type="text"
                placeholder="Ej: Entrada principal, Ventana salón, etc."
                value={formData.ubicacion}
                onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
              />
            </div>

            {/* Fecha de instalación */}
            <div className="space-y-2">
              <Label htmlFor="fecha_instalacion">Fecha de instalación</Label>
              <Input
                id="fecha_instalacion"
                type="date"
                value={formData.fecha_instalacion}
                onChange={(e) => setFormData({ ...formData, fecha_instalacion: e.target.value })}
              />
            </div>

            {/* Marca y Modelo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="marca">Marca</Label>
                <SearchableCombobox
                  value={formData.marca}
                  placeholder="Selecciona marca..."
                  options={marcasDelSistema.map((m) => ({ value: m.nombre, label: m.nombre }))}
                  onChange={(value) => setFormData({ ...formData, marca: value, modelo: '' })}
                  onCreate={async (nombre) => {
                    await crearMarcaEquipo(nombre, sistemaTipo);
                    setFormData({ ...formData, marca: nombre, modelo: '' });
                  }}
                  createLabel={(s) => `+ Añadir marca "${s}"`}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="modelo">Modelo</Label>
                <SearchableCombobox
                  value={formData.modelo}
                  placeholder="Selecciona modelo..."
                  disabled={!selectedMarca}
                  options={modelosDelSistema.map((md) => ({ value: md.nombre, label: md.nombre }))}
                  onChange={(value) => setFormData({ ...formData, modelo: value })}
                  onCreate={async (nombre) => {
                    if (!selectedMarca) return;
                    const tipoEq =
                      formData.tipo_equipo && formData.tipo_equipo !== 'nuevo'
                        ? formData.tipo_equipo
                        : undefined;
                    await crearModeloEquipo(nombre, selectedMarca.id, sistemaTipo, tipoEq);
                    setFormData({ ...formData, modelo: nombre });
                  }}
                  createLabel={(s) => `+ Añadir modelo "${s}"`}
                />
              </div>
            </div>

            {/* Nº Serie / MAC */}
            <div className="space-y-2">
              <Label htmlFor="mac_serie">Nº Serie / MAC</Label>
              <div className="flex gap-2">
                <Input
                  id="mac_serie"
                  placeholder="Nº de serie, MAC, etc."
                  value={formData.mac_serie}
                  onChange={(e) => setFormData({ ...formData, mac_serie: e.target.value })}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setQrDialogAbierto(true)}
                  className="min-w-[44px] min-h-[44px] shrink-0"
                  title="Escanear código QR"
                >
                  <ScanLine className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Año de fabricación */}
            <div className="space-y-2">
              <Label htmlFor="anio_fabricacion">Año de fabricación</Label>
              <Input
                id="anio_fabricacion"
                placeholder="AAAA o MM/AAAA (ej: 2024 o 03/2024)"
                value={formData.anio_fabricacion}
                onChange={(e) => setFormData({ ...formData, anio_fabricacion: e.target.value })}
              />
            </div>

            {/* Datos técnicos específicos del tipo */}
            <DatosTecnicosFields
              tipoEquipo={
                formData.tipo_equipo && formData.tipo_equipo !== 'nuevo'
                  ? formData.tipo_equipo
                  : undefined
              }
              fields={getFieldsForTipo(formData.tipo_equipo)}
              values={formData.datos_tecnicos || {}}
              onChange={(next) => setFormData({ ...formData, datos_tecnicos: next })}
            />

            {/* Observaciones */}
            <div className="space-y-2">
              <Label htmlFor="observaciones">Observaciones</Label>
              <Textarea
                id="observaciones"
                placeholder="Observaciones adicionales..."
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                rows={3}
              />
            </div>

            {/* Estado */}
            <div className="space-y-2">
              <Label htmlFor="estado">Estado</Label>
              <Select
                value={formData.estado}
                onValueChange={(value) => setFormData({ ...formData, estado: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="inactivo">Inactivo</SelectItem>
                  <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                  <SelectItem value="averiado">Averiado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {editandoId ? 'Actualizar' : 'Crear'} elemento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <QRScannerDialog
        open={qrDialogAbierto}
        onOpenChange={(o) => {
          setQrDialogAbierto(o);
          if (!o) {
            qrJustClosedRef.current = true;
            setTimeout(() => {
              qrJustClosedRef.current = false;
            }, 500);
          }
        }}
        onScanSuccess={(decodedText) => {
          const parsed = parseExtintorQR(decodedText);
          const camposRellenados: string[] = [];
          const camposNoMapeados: string[] = [];
          const updates: Partial<typeof formData> = {};

          if (parsed.serie) {
            updates.mac_serie = parsed.serie;
            camposRellenados.push(`Nº Serie: ${parsed.serie}`);
          }
          if (parsed.anio) {
            updates.anio_fabricacion = parsed.anio;
            camposRellenados.push(`Año fab.: ${parsed.anio}`);
          }
          if (parsed.marca) {
            const marcaMatch = marcasDelSistema.find(
              (m) => normalizar(m.nombre) === normalizar(parsed.marca!),
            );
            if (marcaMatch) {
              updates.marca = marcaMatch.nombre;
              camposRellenados.push(`Marca: ${marcaMatch.nombre}`);
            } else {
              camposNoMapeados.push(`Marca "${parsed.marca}" no encontrada`);
            }
          }
          if (parsed.modelo) {
            const modeloMatch = modelosDelSistema.find(
              (md) => normalizar(md.nombre) === normalizar(parsed.modelo!),
            );
            if (modeloMatch) {
              updates.modelo = modeloMatch.nombre;
              camposRellenados.push(`Modelo: ${modeloMatch.nombre}`);
            } else {
              camposNoMapeados.push(`Modelo "${parsed.modelo}" no encontrado`);
            }
          }
          if (camposNoMapeados.length > 0) {
            const obsExtra = `[QR: ${parsed.raw}]`;
            updates.observaciones = formData.observaciones
              ? `${formData.observaciones}\n${obsExtra}`
              : obsExtra;
          }
          setFormData((prev) => ({ ...prev, ...updates }));

          const desc =
            camposRellenados.length > 0
              ? camposRellenados.join(' | ')
              : 'No se pudieron mapear campos automáticamente';
          const fullDesc =
            camposNoMapeados.length > 0
              ? `${desc}\n⚠️ ${camposNoMapeados.join(', ')}`
              : desc;
          toast({ title: 'Código escaneado', description: fullDesc });
        }}
      />
    </>
  );
};
